import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import mailTemplateCore from "../../core/mailTemplate.core";
import { useTranslation } from "../../core/translation.core";
import { MailTemplate, MailTemplateType } from "../../types/mailTemplate";
import FormAlert from "../reuseables/alert.comp";
import { EditingMode, FancySelect, LabelAndInput, LabelAndTextArea } from "../reuseables/form.comp";
import MailList from "./mailList.comp";
import { SubmitPanel } from "../reuseables/editor.comp";

export default function MailTemplateEditor({ editingMode, templateId }: { editingMode: EditingMode; templateId?: string }) {
  const methods = useForm<MailTemplate>();
  const router = useRouter();
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState("");
  const handleAlertReset = () => setErrorMessage("");

  useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line
  }, [templateId]);

  async function fetchTemplate() {
    if (templateId === undefined) return;
    if (editingMode !== EditingMode.edit) return;
    const data = await mailTemplateCore.getMailTemplate(templateId);
    methods.reset(data);
  }

  const onSubmit = async (data: MailTemplate) => {
    switch (editingMode) {
      case EditingMode.create:
        await mailTemplateCore.createMailTemplate(data);
        queryClient.invalidateQueries({ queryKey: ["mailTemplates"] });
        router.push(`/mailTemplates`);
        break;
      case EditingMode.edit:
        await mailTemplateCore.updateMailTemplate(router.query.id as string, data);
        queryClient.invalidateQueries({ queryKey: ["mailTemplate", router.query.id] });
        router.push(`/mailTemplate/${router.query.id}`);
        break;
      default:
        break;
    }
  };

  const templateTypes: MailTemplateType[] = [
    "cartReminder",
    "orderCreated",
    "orderConfirmed",
    "orderCancelled",
    "orderAutoCancelled",
    "orderShipped",
    "orderDelivered",
    "deliveryLogisticDelivered",
    "pickupCompleted",
    "deliveryPreparing",
    "deliveryCanPickup",
    "paymentRefunded",
    "passwordReset",
    "emailVerification",
    "orderConfirmedAdmin",
    "orderCancelledAdmin",
    "formSubmittedAdmin",
    "inStockNotificationAdmin",
  ];

  return (
    <>
      <FormAlert errorMessage={errorMessage} handleReset={handleAlertReset} />
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="mb-20">
          {editingMode === EditingMode.create ? (
            <FancySelect
              className="w-full border-gray-200 bg-transparent"
              register={methods.register}
              fieldName="templateType"
              options={templateTypes.map((templateType) => ({
                label: mailTemplateCore.getMailTemplateTypeTitle(templateType, { t, msg }),
                value: templateType,
              }))}
            />
          ) : (
            <div className="space-y-2">
              <span>{t(msg.mailTemplate.type)}: </span>
              <span className="font-bold">{methods.getValues("templateType")}</span>
            </div>
          )}
          <div className="mt-4">
            <LabelAndInput label={t(msg.mailTemplate.subject)} register={methods.register} fieldName="subject" />
          </div>

          <LabelAndTextArea label={t(msg.mailTemplate.body)} rows={30} register={methods.register} fieldName="body" />
          {methods.watch("templateType")?.includes("Admin") && (
            <div className="grid grid-cols-3 gap-8">
              <MailList fieldName="toList" label="Main Recipients" methods={methods} />
              <MailList fieldName="ccList" label="CC Recipients" methods={methods} />
              <MailList fieldName="bccList" label="BCC Recipients" methods={methods} />
            </div>
          )}
          <SubmitPanel>
            <input type="submit" value={t(msg.form.update)} />
          </SubmitPanel>
        </div>
      </form>
    </>
  );
}
